import { Map, Set } from 'immutable';
import { v4 } from 'uuid';

import { getAttribute, getName, getUiState } from '@/utils/taxonomy';
import { getFirstValue, getPropertyType } from '@/utils/taxonomy/ebay/properties';
import { getCategoryError } from '@/utils/validations/details';
import { toLowerCase } from '@/utils/string';
import { getSize } from '@/utils/iterable/getSize';
import { reduce } from '@/utils/iterable/reduce';
import { get } from '@/utils/iterable/get';

import { DEFAULT_VALUES, ERROR, TAXONOMY_PROPERTY, VALUE } from '@/constants/product';
import { PLACEHOLDERS } from '@/constants/forms';
import { EMPTY, NONE } from '@/constants';
import { ATTRIBUTE } from '@/constants/attributes';
import { VARIANTS } from '@/constants/variations';
import { DATA } from '@/data';

export function getOversetAttributes(variations = EMPTY.MAP) {
  function reduceVariants(result, variant) {
    return result.add(variant.get('property'));
  }

  return variations
    .get(VARIANTS, EMPTY.LIST)
    .reduce(reduceVariants, EMPTY.SET);
}

export function getOversetItemSpecifics({ properties = {}, variations = EMPTY.MAP }) {
  function reduceProperties(result, propertyName) {
    return result.set(toLowerCase(propertyName), propertyName);
  }

  const propertyNames = reduce(Object.keys(properties), EMPTY.MAP, reduceProperties);

  function reduceVariants(result, variant) {
    const lowerCaseName = toLowerCase(variant.get('name'));

    return propertyNames.has(lowerCaseName)
      ? result.add(propertyNames.get(lowerCaseName))
      : result;
  }

  return variations
    .get(VARIANTS, EMPTY.LIST)
    .reduce(reduceVariants, EMPTY.SET);
}

export function getAttributesForCategory({ category, channel, taxonomyAttributes }) {
  if (!category) return { taxonomyAttributes };

  const { availableAttributes: attributes = [] } = getUiState({ channel, taxonomyId: category }, false);

  if (getSize(attributes) === 0) {
    return getSize(taxonomyAttributes.delete(ERROR)) > 0
      ? { changed: true, taxonomyAttributes: EMPTY.MAP }
      : { taxonomyAttributes };
  }

  function filterValueIds(options) {
    return function filter(valueId) {
      function findOption({ id }) {
        return id === valueId;
      }

      return options.findIndex(findOption) !== -1;
    };
  }

  function findScale(scaleId) {
    return function find({ id }) {
      return id === scaleId;
    };
  }

  function filterEmpty(values) {
    return (
      values.get('scaleId', null) ||
      getSize(values.get('values', EMPTY.LIST)) ||
      getSize(values.get('valueIds', EMPTY.LIST))
    );
  }

  function reduceAttributes(result, attribute) {
    const { id: propertyId } = attribute;

    if (result.previous.has(propertyId)) {
      const valueIds = result.previous.getIn([propertyId, 'valueIds'], EMPTY.LIST);
      const scaleId = result.previous.getIn([propertyId, 'scaleId'], null);
      const { availableOptions, availableScales } = getAttribute({
        category,
        channel,
        property: propertyId,
        scale: scaleId,
      });

      const newScaleId = !scaleId && getSize(availableScales) === 0
        ? scaleId
        : availableScales.find(findScale(scaleId))?.id || null;

      if (newScaleId !== scaleId) {
        result.changed = true;
      } else {
        const newValueIds = valueIds.filter(filterValueIds(availableOptions));

        if (newValueIds.size !== valueIds.size) {
          result.changed = true;
        } else {
          result.current = result.current.set(propertyId, result.previous.get(propertyId));
          result.previous = result.previous.delete(propertyId);
        }
      }
    }

    return result;
  }

  const { changed, current, previous } = attributes.reduce(reduceAttributes, {
    previous: taxonomyAttributes,
    current: EMPTY.MAP,
  });

  return { changed: changed || !!previous.filter(filterEmpty).size, taxonomyAttributes: current };
}

export function updateItemSpecificsForCategory({ category, channel, itemSpecifics: source, marketplaceId }) {
  if (
    getCategoryError({ category, channel, marketplaceId }) ||
    get([channel, marketplaceId, 'CATEGORIES', category, 'features', 'itemSpecifics'])(DATA) === false
  ) {
    return source;
  }

  const taxonomyProperties = get([channel, marketplaceId, 'CATEGORIES', category, 'properties'])(DATA);

  if (!taxonomyProperties) return source;

  function reduceItemSpecifics(result, item) {
    let itemSpecifics = result;
    let specific = item;
    const property = taxonomyProperties[specific.get('name')];
    const type = getPropertyType(property);

    if (specific.get('type') !== type) {
      specific = specific.set('type', type);
    }

    const isCustom = type === TAXONOMY_PROPERTY.CUSTOM;

    if (isCustom) {
      if (!specific.has('id')) {
        specific = specific.set('id', v4());
      }

      if (getSize(specific.get('value')) > 0) {
        specific = specific.set('value', Set([getFirstValue(specific.get(VALUE))]));
      }

      if (specific.get('required')) {
        specific = specific.delete('required');
      }
    } else {
      if (specific.has('id')) {
        specific = specific.delete('id');
      }

      if (specific.get('required') !== property.required) {
        specific = specific.set('required', property.required);
      }
    }

    const key = isCustom
      ? specific.get('id')
      : specific.get('name');

    if (isCustom) {
      itemSpecifics = itemSpecifics.set('custom', itemSpecifics.get('custom', EMPTY.LIST).push(key));
    }

    return itemSpecifics.setIn([VALUE, key], specific);
  }

  return source
    .get(VALUE, EMPTY.MAP)
    .reduce(reduceItemSpecifics, Map({ custom: EMPTY.LIST, [VALUE]: EMPTY.MAP }));
}

export function updateProductDetailsForCategory({ category, channel, marketplaceId, productDetails: source }) {
  if (getCategoryError({ category, channel, marketplaceId }) || !get([channel, marketplaceId, 'CATEGORIES', category])(DATA)) {
    return source;
  }

  const taxonomyDetails = get([channel, marketplaceId, 'CATEGORIES', category, 'productDetails'])(DATA);

  if (getSize(taxonomyDetails) === 0) {
    return DEFAULT_VALUES.EMPTY_MAP;
  }

  function reduceProductDetails(result, item) {
    let detail = item;
    const property = taxonomyDetails[item.get('name')];
    const type = getPropertyType(property);

    if (detail.get('type') !== type) {
      detail = detail.set('type', type);
    }

    if (detail.get('required') !== property.required) {
      detail = detail.set('required', property.required);
    }

    return result.setIn([VALUE, detail.get('name')], detail);
  }

  return source
    .get(VALUE, EMPTY.MAP)
    .reduce(reduceProductDetails, Map({ [VALUE]: EMPTY.MAP }));
}

export const messages = {
  definedByCategory(attribute) {
    return `${attribute} defined by category`;
  },
  definedByVariations(attribute) {
    return `${attribute} defined by variations`;
  },
  notAvailable({ attribute, category, channel }) {
    const name = getName({ category, channel });
    return name === NONE || name === PLACEHOLDERS[ATTRIBUTE.CATEGORY]
      ? `${attribute} is not available`
      : `${attribute} is not available for ${name}`;
  },
};
